import axios from 'axios';
import config from '../config';

// Public tools API — no auth token attached; separate from the main authenticated api.ts
const baseURL = config.VITE_PUBLIC_TOOLS_API_BASE_URL.replace(/\/$/, '');

export const publicToolsApi = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json', sourceSystem: 'CUSTOMER_PORTAL' },
  timeout: 30_000,
});

// ── Types ────────────────────────────────────────────────────────────────────

export type ScanType =
  | 'SITE'
  | 'PAGE'
  | 'ROBOTS'
  | 'SITEMAP'
  | 'GSC_OWNER'
  | 'SCHEMA'
  | 'AI_CRAWLING'
  | 'GA4';

export type CheckStatus = 'PASS' | 'FAIL' | 'WARNING' | 'INFO';
export type CheckSeverity = 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';

export interface BrandingAnalyzeRequest {
  name?: string;
  email?: string;
  businessName?: string;
  url: string;
  scanType: ScanType;
  /** Optional: explicit sitemap URL to validate instead of auto-discovery. SITEMAP scans only. */
  sitemapUrl?: string;
  /** Optional: raw sitemap XML content uploaded by the user. SITEMAP scans only. Max 1 MB. */
  xmlContent?: string;
}

export interface CheckResult {
  category: string;
  checkName: string;
  status: CheckStatus;
  severity: CheckSeverity;
  message: string;
  recommendation?: string;
  score?: number;
}

export interface BrandingAnalyzeResponse {
  scanId: string;
  inputUrl: string;
  normalizedUrl: string;
  domain: string;
  status: string;
  overallScore: number;
  overallGrade: string;
  summary: string;
  checks: CheckResult[];
  completedAt: string;
  customerName?: string;
  customerEmail?: string;
  businessName?: string;
}

export interface ApiError {
  status: number;
  error: string;
  messages: string[];
}

// ── API calls ────────────────────────────────────────────────────────────────

export async function analyzeUrl(
  request: BrandingAnalyzeRequest,
  signal?: AbortSignal,
): Promise<BrandingAnalyzeResponse> {
  const response = await publicToolsApi.post<BrandingAnalyzeResponse>(
    '/api/public/branding/analyze',
    request,
    { signal },
  );
  return response.data;
}

/**
 * Polls the status of a background scan (one that returned HTTP 202).
 * Returns the full report when status === 'COMPLETED'.
 */
export async function pollScanStatus(scanId: string): Promise<BrandingAnalyzeResponse> {
  const response = await publicToolsApi.get<BrandingAnalyzeResponse>(
    `/api/public/branding/scan/${scanId}`,
  );
  return response.data;
}

export async function sendReportByEmail(scanId: string, email: string): Promise<void> {
  await publicToolsApi.post('/api/public/branding/report/email', { scanId, email });
}

// ── Consolidated report ───────────────────────────────────────────────────────

export interface ConsolidatedReportCreateResponse {
  referenceNumber: string;
  message: string;
  estimatedReadyAt: string; // ISO datetime
  name?: string;
  businessName?: string;
}

export type ReportState =
  | 'PROCESSING_WAIT'
  | 'PROCESSING_DELAYED'
  | 'READY'
  | 'FAILED';

export interface ConsolidatedReportStatusResponse {
  reportState: ReportState;
  message: string;
  referenceNumber: string;
  estimatedReadyAt?: string;
  completedAt?: string;
  emailSent?: boolean;
  name?: string;
  businessName?: string;
  /** Present when reportState === 'READY'. Key = ScanType string. */
  scanResults?: Record<string, BrandingAnalyzeResponse>;
}

export async function requestConsolidatedReport(
  name: string,
  url: string,
  email: string,
  businessName: string,
): Promise<ConsolidatedReportCreateResponse> {
  const resp = await publicToolsApi.post<ConsolidatedReportCreateResponse>(
    '/api/public/branding/report/consolidated/request',
    { name, url, email, businessName },
  );
  return resp.data;
}

export async function checkConsolidatedReportStatus(
  referenceNumber: string,
  email: string,
): Promise<ConsolidatedReportStatusResponse> {
  const resp = await publicToolsApi.post<ConsolidatedReportStatusResponse>(
    '/api/public/branding/report/consolidated/status',
    { referenceNumber, email },
  );
  return resp.data;
}

// ── Contact Enquiry ──────────────────────────────────────────────────────────

export interface ContactConfig {
  queryTypes: string[];
  languages: { code: string; label: string }[];
  timezones: string[];
}

export interface ContactEnquiryRequest {
  sourceSystem?: string;
  queryType: string;
  name: string;
  email: string;
  contactNumber?: string;
  preferredLanguage?: string;
  preferredTimezone?: string;
  preferredHour?: number;
  detail?: string;
}

export async function getContactConfig(sourceSystem?: string): Promise<ContactConfig> {
  const params = sourceSystem?.trim() ? { sourceSystem: sourceSystem.trim() } : undefined;
  const resp = await publicToolsApi.get<ContactConfig>('/api/public/contact/config', { params });
  return resp.data;
}

export async function submitContactEnquiry(req: ContactEnquiryRequest): Promise<{ success: boolean; id: string; message: string }> {
  const resp = await publicToolsApi.post('/api/public/contact/submit', req);
  return resp.data;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function isRateLimitError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 429;
}

export function extractErrorMessages(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 429) {
      return 'Too many requests. Please wait a moment and try again.';
    }
    const data = err.response?.data as Partial<ApiError> | undefined;
    if (data?.messages?.length) return data.messages.join(' ');
    if (data?.error) return data.error;
    if (err.response?.status === 400) return 'Invalid URL or request. Please check and try again.';
    if (err.response?.status && err.response.status >= 500)
      return 'The analysis service is temporarily unavailable. Please try again shortly.';
    if (err.code === 'ERR_CANCELED') return '';
  }
  return 'An unexpected error occurred. Please try again.';
}
